import React, { useState, useEffect, useRef } from 'react';
import {
  Typography, Button, Box, TextField, Switch, Collapse, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import LayersClearIcon from '@mui/icons-material/LayersClear';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import { getNotificationSettings, updateNotificationSettings, clearAllDetections, resetScanHistory, resetStreamCounter, resetEverything, triggerReclassify, testNotification } from '../services/api';

function fmtDuration(seconds) {
  const abs = Math.abs(seconds);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  if (m > 0) return `${m}m`;
  return 'just now';
}

function fmtClock(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${m}:${String(ss).padStart(2, '0')}`;
}

const NOTIFY_TOGGLES = [
  { key: 'notify_new_active', label: 'New Active Domains' },
  { key: 'notify_classification_change', label: 'Classification Changes' },
  { key: 'notify_new_detections', label: 'New Detections Summary' },
  { key: 'notify_domain_disappeared', label: 'Domain Disappeared' },
];

const INTERVAL_PRESETS = [2, 4, 6, 8, 12, 24];

/* Overall scan completion 0..1 from the progress payload, or null when the
   shape is not there yet (indeterminate ring). Six phases per domain. */
function overallFraction(p) {
  if (!p || !p.domain_total || !p.phase) return null;
  const phaseFrac = (p.phase - 1 + (p.phase_total > 0 ? Math.min(1, p.phase_done / p.phase_total) : 0)) / 6;
  const frac = ((p.domain_index ? p.domain_index - 1 : 0) + Math.min(1, phaseFrac)) / p.domain_total;
  return Math.max(0, Math.min(1, frac));
}

function ScanControl({
  scanning, scanProgress, runningScan, avgScanSeconds,
  onTriggerScan, onCancelScan,
  schedEnabled, schedInterval,
  onScheduleChange,
  lastCompletedScan, nextAutoTs,
  showNotification, onClearHistory,
}) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [showScanSettings, setShowScanSettings] = useState(false);
  const [busyAction, setBusyAction] = useState(null);   // key of the reset currently running
  const [reclassifying, setReclassifying] = useState(false);
  const [confirmCfg, setConfirmCfg] = useState(null);   // {key,title,body,confirmLabel,requireText,run}

  // Countdown timer for Next Auto display
  const [countdownText, setCountdownText] = useState('—');

  useEffect(() => {
    const update = () => {
      if (!schedEnabled) { setCountdownText('Off'); return; }
      if (!nextAutoTs) { setCountdownText('—'); return; }
      const secs = Math.round((nextAutoTs - Date.now()) / 1000);
      setCountdownText(secs >= 60 ? 'in ' + fmtDuration(secs) : 'soon');
    };
    update();
    const id = setInterval(update, 10000);
    return () => clearInterval(id);
  }, [nextAutoTs, schedEnabled]);

  // Elapsed clock for the running scan (1s tick, only while scanning)
  const [elapsed, setElapsed] = useState(0);
  const runningScanId = runningScan?.id;
  const runningScanDate = runningScan?.scan_date;
  useEffect(() => {
    if (!scanning || !runningScanDate) { setElapsed(0); return; }
    const started = new Date(runningScanDate + 'Z').getTime();
    const tick = () => setElapsed(Math.max(0, (Date.now() - started) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [scanning, runningScanId, runningScanDate]);

  // Notification settings state
  const [notifySettings, setNotifySettings] = useState(null);
  const [notifyDirty, setNotifyDirty] = useState(false);
  const [notifySaving, setNotifySaving] = useState(false);

  useEffect(() => {
    getNotificationSettings()
      .then(({ data }) => setNotifySettings(data))
      .catch(() => {});
  }, []);

  const setNotifyField = (key, value) => {
    setNotifySettings(prev => ({ ...prev, [key]: value }));
    setNotifyDirty(true);
  };

  // Latest form state, so a save can tell whether the user kept typing
  const notifyLatestRef = useRef(notifySettings);
  notifyLatestRef.current = notifySettings;

  const handleSaveNotify = async () => {
    setNotifySaving(true);
    const sent = notifySettings;
    try {
      const { data } = await updateNotificationSettings(sent);
      if (notifyLatestRef.current === sent) {
        setNotifySettings(data);
        setNotifyDirty(false);
        showNotification('Notification settings saved.', 'success');
      } else {
        // edits typed during the save stay on screen and stay unsaved
        showNotification('Saved. Newer edits are not saved yet.', 'info');
      }
    } catch {
      showNotification('Error saving notification settings.', 'error');
    } finally {
      setNotifySaving(false);
    }
  };

  const runReset = async (key, fn, successMsg) => {
    setBusyAction(key);
    try {
      const { data } = await fn();
      showNotification(successMsg(data), 'success');
      if (onClearHistory) onClearHistory(key);
    } catch (e) {
      showNotification(e.response?.data?.detail || 'Error resetting data.', 'error');
    } finally {
      setBusyAction(null);
      setConfirmCfg(null);
    }
  };

  const DM_ACTIONS = [
    {
      key: 'findings', label: 'Reset Findings', icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />,
      caption: 'Clear all detected domains, keep scan history',
      confirm: {
        title: 'Reset findings?',
        body: 'Delete all detected domains (findings). Scan history is kept. This cannot be undone.',
        confirmLabel: 'Delete findings',
        run: () => runReset('findings', clearAllDetections,
          d => `Cleared ${d.deleted} finding${d.deleted !== 1 ? 's' : ''}.`),
      },
    },
    {
      key: 'history', label: 'Reset Scan History', icon: <HistoryToggleOffIcon sx={{ fontSize: 16 }} />,
      caption: 'Clear scan history, keep findings',
      confirm: {
        title: 'Reset scan history?',
        body: 'Delete all scan history. Findings are kept but lose their scan link. This cannot be undone.',
        confirmLabel: 'Delete history',
        run: () => runReset('history', resetScanHistory,
          d => `Cleared ${d.scans_deleted} scan${d.scans_deleted !== 1 ? 's' : ''} from history.`),
      },
    },
    {
      key: 'stream', label: 'Reset Brand Matches', icon: <RefreshIcon sx={{ fontSize: 16 }} />,
      caption: 'Zero the live stream running totals',
      confirm: {
        title: 'Reset stream totals?',
        body: 'Zero the live stream running totals (brand matches + scanned). Findings are not touched.',
        confirmLabel: 'Reset totals',
        run: () => runReset('stream', resetStreamCounter, () => 'Stream running totals reset.'),
      },
    },
    {
      key: 'everything', label: 'Reset Everything', icon: <LayersClearIcon sx={{ fontSize: 16 }} />,
      caption: 'Delete all findings and scan history', danger: true,
      confirm: {
        title: 'Reset EVERYTHING?',
        body: 'Delete ALL findings AND all scan history, and zero the stream totals. This is a full reset and cannot be undone.',
        confirmLabel: 'Reset everything',
        requireText: 'RESET',
        run: () => runReset('everything', resetEverything,
          d => `Reset complete: ${d.detections_deleted} findings, ${d.scans_deleted} scans removed.`),
      },
    },
  ];

  const handleReclassify = async () => {
    setReclassifying(true);
    try {
      const { data } = await triggerReclassify();
      showNotification(data.message, 'info');
    } catch (e) {
      showNotification(e.response?.data?.detail || 'Error starting reclassification.', 'error');
    } finally {
      setReclassifying(false);
    }
  };

  const handleTestNotify = async () => {
    try {
      const { data } = await testNotification();
      if (data.errors?.length) showNotification(`Test errors: ${data.errors.join(', ')}`, 'error');
      else if (data.sent?.length) showNotification(`Test sent via: ${data.sent.join(', ')}`, 'success');
      else showNotification('No delivery channels configured.', 'warning');
    } catch {
      showNotification('Test request failed.', 'error');
    }
  };

  const toggleSx = {
    transform: 'scale(0.75)', ml: -0.5,
    '& .MuiSwitch-switchBase.Mui-checked': { color: '#4ade80' },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'rgba(74,222,128,0.4)' },
    '& .MuiSwitch-switchBase:not(.Mui-checked)': { color: '#d97068' },
    '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track': { bgcolor: 'rgba(217,112,104,0.4)' },
  };

  const overall = overallFraction(scanProgress);
  const remaining = (avgScanSeconds && elapsed > 0) ? avgScanSeconds - elapsed : null;

  return (
    <Box sx={{ px: 2.5, py: 2.5 }}>
      {/* Run / progress + cancel */}
      {scanning ? (
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.4, p: 1.2, borderRadius: 2,
            bgcolor: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.22)',
          }}>
            <Box sx={{ position: 'relative', width: 38, height: 38, flexShrink: 0 }}>
              <CircularProgress
                variant={overall != null ? 'determinate' : 'indeterminate'}
                value={overall != null ? overall * 100 : undefined}
                size={38} thickness={3.6}
                sx={{ color: '#fb923c', '& .MuiCircularProgress-circle': { transition: 'stroke-dashoffset 0.9s linear' } }}
              />
              {overall != null && (
                <Typography sx={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '0.58rem', fontWeight: 800,
                  color: '#fb923c', fontVariantNumeric: 'tabular-nums',
                }}>
                  {Math.round(overall * 100)}%
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#fb923c',
                                textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Scanning
              </Typography>
              <Typography sx={{ fontSize: '0.66rem', color: 'rgba(232,228,248,0.45)',
                                fontVariantNumeric: 'tabular-nums' }}>
                {fmtClock(elapsed)} elapsed
                {remaining != null && (remaining >= 60
                  ? ` · ~${fmtDuration(remaining)} left`
                  : ' · wrapping up')}
              </Typography>
            </Box>
          </Box>
          <Button
            fullWidth size="small"
            onClick={onCancelScan}
            sx={{
              mt: 1, py: 0.7,
              bgcolor: 'rgba(248,113,113,0.12)', color: '#f87171',
              '&:hover': { bgcolor: 'rgba(248,113,113,0.22)' },
            }}
          >
            Cancel Scan
          </Button>
        </Box>
      ) : (
        <Button
          fullWidth variant="contained" startIcon={<PlayArrowIcon />}
          onClick={onTriggerScan}
          sx={{ mb: 2.5, py: 1.2 }}
        >
          Run Scan
        </Button>
      )}

      {/* Last / Next timing */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
        <Box sx={{ flex: 1 }}>
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <AccessTimeIcon sx={{ fontSize: 11, color: 'rgba(232,228,248,0.25)' }} />
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
                              letterSpacing: 0.5, color: 'rgba(232,228,248,0.25)' }}>
              Last Scan
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#e8e4f8', lineHeight: 1.3 }}>
            {lastCompletedScan
              ? (() => { const d = fmtDuration(Math.round((new Date().getTime() - (
                  lastCompletedScan.duration_seconds
                    ? new Date(lastCompletedScan.scan_date + 'Z').getTime() + lastCompletedScan.duration_seconds * 1000
                    : new Date(lastCompletedScan.scan_date + 'Z').getTime()
                )) / 1000)); return d === 'just now' ? d : d + ' ago'; })()
              : '—'}
          </Typography>
          {lastCompletedScan && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.3 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'rgba(232,228,248,0.2)' }}>
                {lastCompletedScan.total_findings || lastCompletedScan.new_detections || 0} found
                {(lastCompletedScan.new_detections > 0) && ` · ${lastCompletedScan.new_detections} new`}
              </Typography>
              {Boolean(lastCompletedScan.partial_data) && (
                <Box component="span" sx={{
                  px: 0.6, py: 0.05, borderRadius: 1, fontSize: '0.56rem', fontWeight: 700,
                  bgcolor: 'rgba(251,191,36,0.14)', color: '#fbbf24',
                  border: '1px solid rgba(251,191,36,0.25)', lineHeight: 1.4,
                }}>
                  partial
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <ScheduleIcon sx={{ fontSize: 11, color: 'rgba(232,228,248,0.25)' }} />
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
                              letterSpacing: 0.5, color: 'rgba(232,228,248,0.25)' }}>
              Next Auto
            </Typography>
          </Box>
          <Typography sx={{
            fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.3,
            color: !schedEnabled ? 'rgba(232,228,248,0.25)' : '#e8e4f8',
          }}>
            {countdownText}
          </Typography>
        </Box>
      </Box>

      {/* ── Schedule Settings (collapsible) ──────────────── */}
      <CollapsibleSection
        icon={<SettingsIcon sx={{ fontSize: 13 }} />}
        label="Schedule Settings"
        open={showSchedule}
        onToggle={() => setShowSchedule(!showSchedule)}
      >
        <Box sx={{ p: 1.5, bgcolor: 'rgba(124,92,191,0.06)',
                   border: '1px solid rgba(124,92,191,0.15)', borderRadius: 2 }}>
          {/* Enable toggle */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600,
                              color: schedEnabled ? '#4ade80' : 'rgba(232,228,248,0.3)' }}>
              {schedEnabled ? 'Auto-scan On' : 'Auto-scan Off'}
            </Typography>
            <Switch
              checked={schedEnabled}
              onChange={(e) => onScheduleChange({ enabled: e.target.checked })}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#4ade80' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'rgba(74,222,128,0.3)' },
                '& .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            />
          </Box>

          <Box sx={{ opacity: schedEnabled ? 1 : 0.4, pointerEvents: schedEnabled ? 'auto' : 'none' }}>
            <Typography sx={{ fontSize: '0.67rem', color: 'rgba(232,228,248,0.3)', mb: 1 }}>
              Scan every
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.75,
                       gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' } }}>
              {INTERVAL_PRESETS.map((hours) => (
                <Button
                  key={hours}
                  size="small"
                  variant={schedInterval === hours ? 'contained' : 'outlined'}
                  onClick={() => onScheduleChange({ interval_hours: hours })}
                  sx={{
                    minWidth: 0, py: 0.6, fontSize: '0.75rem', fontWeight: 600,
                    ...(schedInterval === hours
                      ? {
                          bgcolor: 'rgba(167,139,250,0.2)', color: '#a78bfa',
                          border: '1px solid rgba(167,139,250,0.4)',
                          '&:hover': { bgcolor: 'rgba(167,139,250,0.3)' },
                        }
                      : {
                          color: 'rgba(232,228,248,0.4)',
                          borderColor: 'rgba(255,255,255,0.08)',
                          '&:hover': { borderColor: 'rgba(167,139,250,0.3)', color: '#a78bfa',
                                       bgcolor: 'rgba(167,139,250,0.05)' },
                        }),
                  }}
                >
                  {hours}h
                </Button>
              ))}
            </Box>
            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(232,228,248,0.15)', mt: 1, textAlign: 'center' }}>
              {Math.floor(24 / schedInterval)} scans per day
            </Typography>
          </Box>
        </Box>
      </CollapsibleSection>

      {/* ── Notification Settings (collapsible) ──────────── */}
      <CollapsibleSection
        icon={<NotificationsIcon sx={{ fontSize: 13 }} />}
        label="Notification Settings"
        open={showNotify}
        onToggle={() => setShowNotify(!showNotify)}
      >
        {notifySettings ? (
          <Box sx={{ p: 1.5, bgcolor: 'rgba(124,92,191,0.06)',
                     border: '1px solid rgba(124,92,191,0.15)', borderRadius: 2 }}>

            {/* Event toggles */}
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
                              letterSpacing: 0.5, color: 'rgba(232,228,248,0.25)', mb: 1 }}>
              Alert Events
            </Typography>
            {NOTIFY_TOGGLES.map(({ key, label }) => (
              <Box key={key} display="flex" alignItems="center" justifyContent="space-between"
                   sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.72rem', color: '#c8c0e8' }}>{label}</Typography>
                <Switch checked={!!notifySettings[key]} onChange={() => setNotifyField(key, !notifySettings[key])}
                  size="small" sx={toggleSx} />
              </Box>
            ))}

            {/* Webhook */}
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
                              letterSpacing: 0.5, color: 'rgba(232,228,248,0.25)', mt: 2, mb: 1 }}>
              Webhook
            </Typography>
            <TextField
              fullWidth size="small" placeholder="https://hooks.slack.com/..."
              value={notifySettings.webhook_url || ''}
              onChange={e => setNotifyField('webhook_url', e.target.value)}
              sx={fieldSx}
            />

            {/* Email */}
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
                              letterSpacing: 0.5, color: 'rgba(232,228,248,0.25)', mt: 2, mb: 1 }}>
              Email (SMTP)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField size="small" placeholder="smtp.gmail.com"
                value={notifySettings.smtp_host || ''}
                onChange={e => setNotifyField('smtp_host', e.target.value)}
                sx={{ ...fieldSx, flex: 3 }} />
              <TextField size="small" placeholder="587" type="number"
                value={notifySettings.smtp_port || 587}
                onChange={e => setNotifyField('smtp_port', parseInt(e.target.value) || 587)}
                sx={{ ...fieldSx, flex: 1 }} />
            </Box>
            <TextField fullWidth size="small" placeholder="Username"
              value={notifySettings.smtp_user || ''}
              onChange={e => setNotifyField('smtp_user', e.target.value)}
              sx={{ ...fieldSx, mb: 1 }} />
            <TextField fullWidth size="small" placeholder="Password" type="password"
              value={notifySettings.smtp_pass || ''}
              onChange={e => setNotifyField('smtp_pass', e.target.value)}
              sx={{ ...fieldSx, mb: 1 }} />
            <TextField fullWidth size="small" placeholder="From address"
              value={notifySettings.smtp_from || ''}
              onChange={e => setNotifyField('smtp_from', e.target.value)}
              sx={{ ...fieldSx, mb: 1 }} />
            <TextField fullWidth size="small" placeholder="Recipients (comma-separated)"
              value={notifySettings.smtp_to || ''}
              onChange={e => setNotifyField('smtp_to', e.target.value)}
              sx={fieldSx} />

            {/* Actions */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <Button size="small" onClick={handleTestNotify}
                disabled={notifyDirty || (!notifySettings.webhook_url && !notifySettings.smtp_host)}
                sx={{ color: 'rgba(232,228,248,0.4)', fontSize: '0.68rem', textTransform: 'none',
                      '&:hover': { color: '#a78bfa' } }}>
                {notifyDirty ? 'Save First to Test' : 'Send Test'}
              </Button>
              <Box display="flex" gap={1}>
                {notifyDirty && (
                  <Button size="small" variant="outlined" onClick={handleSaveNotify} disabled={notifySaving}
                    sx={{ color: '#a78bfa', borderColor: 'rgba(167,139,250,0.3)', fontSize: '0.72rem',
                          '&:hover': { borderColor: '#a78bfa', bgcolor: 'rgba(167,139,250,0.08)' } }}>
                    {notifySaving ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(232,228,248,0.3)', py: 1 }}>
            Loading…
          </Typography>
        )}
      </CollapsibleSection>

      {/* ── Scan Settings (collapsible) ────────────────────── */}
      <CollapsibleSection
        icon={<TuneIcon sx={{ fontSize: 13 }} />}
        label="Scan Settings"
        open={showScanSettings}
        onToggle={() => setShowScanSettings(!showScanSettings)}
      >
        <Box sx={{ p: 1.5, bgcolor: 'rgba(124,92,191,0.06)',
                   border: '1px solid rgba(124,92,191,0.15)', borderRadius: 2 }}>

          {/* Reclassify */}
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
                            letterSpacing: 0.5, color: 'rgba(232,228,248,0.25)', mb: 1 }}>
            Classification
          </Typography>
          <Button
            fullWidth size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={handleReclassify}
            disabled={reclassifying || scanning}
            sx={{
              justifyContent: 'flex-start', py: 0.8, fontSize: '0.72rem',
              color: 'rgba(232,228,248,0.5)', textTransform: 'none',
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1.5,
              '&:hover': { color: '#a78bfa', borderColor: 'rgba(167,139,250,0.3)',
                           bgcolor: 'rgba(167,139,250,0.05)' },
            }}
          >
            {reclassifying ? 'Starting...' : 'Reclassify All Domains'}
          </Button>
          <Typography sx={{ fontSize: '0.6rem', color: 'rgba(232,228,248,0.2)', mt: 0.5 }}>
            Re-check all domains with latest classification rules
          </Typography>

          {/* Data Management: the granular reset controls as VISIBLE buttons
              (per spec). Each opens a confirm dialog so nothing fires by
              accident; the destructive full wipe is red. */}
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
                            letterSpacing: 0.5, color: 'rgba(232,228,248,0.25)', mt: 2, mb: 1 }}>
            Data Management
          </Typography>
          {DM_ACTIONS.map((a) => (
            <Box key={a.key} sx={{ mb: 1 }}>
              <Button
                fullWidth size="small"
                startIcon={a.icon}
                onClick={() => setConfirmCfg(a.confirm)}
                disabled={Boolean(busyAction) || (scanning && a.key !== 'stream')}
                sx={{
                  justifyContent: 'flex-start', py: 0.8, fontSize: '0.72rem',
                  color: a.danger ? '#f87171' : 'rgba(232,228,248,0.5)',
                  textTransform: 'none', borderRadius: 1.5,
                  border: `1px solid ${a.danger ? 'rgba(248,113,113,0.25)' : 'rgba(255,255,255,0.08)'}`,
                  '&:hover': {
                    color: '#f87171', borderColor: 'rgba(248,113,113,0.3)',
                    bgcolor: 'rgba(248,113,113,0.05)',
                  },
                }}
              >
                {busyAction === a.key ? 'Working...' : a.label}
              </Button>
              <Typography sx={{ fontSize: '0.6rem', color: 'rgba(232,228,248,0.2)', mt: 0.4, ml: 0.3 }}>
                {a.caption}
              </Typography>
            </Box>
          ))}
        </Box>
      </CollapsibleSection>

      <ConfirmDialog
        open={Boolean(confirmCfg)}
        cfg={confirmCfg}
        busy={Boolean(busyAction)}
        onClose={() => setConfirmCfg(null)}
      />
    </Box>
  );
}

/* Confirm dialog for destructive actions; the nuclear one requires typing RESET */
function ConfirmDialog({ open, cfg, busy, onClose }) {
  const [typed, setTyped] = useState('');
  useEffect(() => { if (open) setTyped(''); }, [open]);
  if (!cfg) return null;
  const armed = !cfg.requireText || typed.trim() === cfg.requireText;
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      PaperProps={{ sx: {
        bgcolor: '#1a1730', border: '1px solid rgba(255,255,255,0.1)',
        backgroundImage: 'none', minWidth: { xs: 'auto', sm: 360 },
      } }}
    >
      <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#e8e4f8', pb: 1 }}>
        {cfg.title}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.8rem', color: 'rgba(232,228,248,0.6)', lineHeight: 1.6 }}>
          {cfg.body}
        </Typography>
        {cfg.requireText && (
          <TextField
            fullWidth size="small" autoFocus
            placeholder={`Type ${cfg.requireText} to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            sx={{ mt: 2, ...fieldSx }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose} disabled={busy}
          sx={{ color: 'rgba(232,228,248,0.5)' }}>
          Cancel
        </Button>
        <Button
          size="small" variant="contained" disabled={!armed || busy}
          onClick={cfg.run}
          sx={{
            bgcolor: 'rgba(248,113,113,0.85)', color: '#fff',
            '&:hover': { bgcolor: '#ef4444' },
            '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.25)' },
          }}
        >
          {busy ? 'Working...' : cfg.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* Shared collapsible section header */
function CollapsibleSection({ icon, label, open, onToggle, children }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', py: 0.8, px: 0.5, mx: -0.5, borderRadius: 1,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
        }}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ color: 'rgba(232,228,248,0.3)' }}>{icon}</Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(232,228,248,0.4)', fontWeight: 600 }}>
            {label}
          </Typography>
        </Box>
        {open
          ? <KeyboardArrowUpIcon sx={{ fontSize: 16, color: 'rgba(232,228,248,0.3)' }} />
          : <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'rgba(232,228,248,0.3)' }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ mt: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

/* Compact text field styling for sidebar */
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.03)', fontSize: '0.75rem',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&.Mui-focused fieldset': { borderColor: '#a78bfa' },
  },
  '& .MuiOutlinedInput-input': { color: '#e8e4f8', py: 0.8, px: 1.2 },
};

export default ScanControl;
